import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Home } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { formatMarkdownToSafeHtml } from "@/lib/sanitize";

interface ShowingsPreviewProps {
  northId: string;
  showingsSummary: string | null;
}

interface ShowingEvent {
  id: string;
  title: string;
  feedback: string;
  scheduled_date: string;
  event_type: string;
  status?: string | null;
}

function getStatusBadge(status: string | null | undefined) {
  if (!status || status === 'completed') return null;
  const labels: Record<string, string> = {
    scheduled: 'Scheduled',
    requested: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
  };
  const classes: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    requested: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
  };
  const label = labels[status] ?? (status.charAt(0).toUpperCase() + status.slice(1));
  const cls = classes[status] ?? 'bg-muted text-muted-foreground';
  return { label, cls };
}

export const ShowingsPreview = ({ northId, showingsSummary }: ShowingsPreviewProps) => {
  const [showAll, setShowAll] = useState(false);
  
  // Check if this is a demo report
  const isDemo = northId === 'aaaaaaaa-0000-0000-0000-000000000001';
  
  const { data: showings, error: showingsError } = useQuery({
    queryKey: ['showings-preview', northId],
    queryFn: async () => {
      if (isDemo) {
        // For demo, fetch from demo_showing_events table
        const { data: demoShowings, error: demoError } = await supabase
          .from('demo_showing_events')
          .select('*')
          .eq('demo_report_id', northId)
          .order('scheduled_date', { ascending: false });
        
        if (demoError) {
          console.error('Error fetching demo showings:', demoError);
          return [];
        }
        
        return (demoShowings || []) as ShowingEvent[];
      }

      // Otherwise fetch from regular table
      const { data, error } = await supabase
        .from('showing_events')
        .select('*')
        .eq('north_id', northId)
        .order('scheduled_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data || []) as ShowingEvent[];
    },
  });
  
  const displayShowings = showings;

  if (!displayShowings || displayShowings.length === 0) {
    return null;
  }

  const displayedShowings = showAll ? displayShowings : displayShowings.slice(0, 5);
  const hasMore = displayShowings.length > 5;

  return (
    <div>
      <div>
        <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">WHO CAME TO SEE YOUR HOME</h3>
        
        {showingsSummary && (
          <div className="mb-6">
            <div
              className="text-foreground/90 leading-relaxed text-base"
              dangerouslySetInnerHTML={{
                __html: formatMarkdownToSafeHtml(showingsSummary)
              }}
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        {displayedShowings.map((showing) => {
          const isCancelled = showing.status === 'cancelled';
          const statusBadge = getStatusBadge(showing.status);
          return (
            <div
              key={showing.id}
              className={`bg-background border border-border rounded-lg p-6 ${isCancelled ? 'opacity-60' : ''}`}
            >
              <div className="flex gap-4">
                {/* Home Icon */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Home className="h-6 w-6 text-foreground" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(showing.scheduled_date), 'M/d/yyyy')}
                    </span>
                    {statusBadge && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge.cls}`}>
                        {statusBadge.label}
                      </span>
                    )}
                  </div>
                  <h4 className={`text-lg font-semibold mb-2 ${isCancelled ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                    {showing.title}
                  </h4>
                  {showing.feedback && (
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {showing.feedback}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {hasMore && !showAll && (
          <div className="flex justify-center pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowAll(true)}
              className="w-full sm:w-auto"
            >
              Load More Showings ({showings.length - 5} more)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
